import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format } from "date-fns";
import {
  User,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  AlertTriangle,
  FileText,
  Activity,
  MessageSquare,
  Save,
  X,
  History,
  UserPlus,
  Clock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { formatCurrency, cn } from "@/lib/utils";
import { toast } from "sonner";
import { type ContactUI } from "@/types/ui/customer-detail";
import { useUsersList } from "@/hooks/useUsersData";
import { useCreateCollectionCase, mapPriorityToId } from "@/hooks/useCollectionCasesData";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { type TransactionType, type Transaction as TransactionFull } from "@/types/transaction";
import { useNavigate } from "react-router-dom";
import { CustomerSearchCombobox } from "@/components/shared/customer-search-combobox";
import type { CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";

interface CreateCaseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCaseCreated?: () => void;
  prePopulatedCustomer?: {
    customerId: string;
    customerName: string;
    accountNumber: string;
  };
  selectedTransactions?: TransactionFull[];
}

interface NewCaseData {
  customerName: string;
  customerId: string;
  customerEmail: string;
  customerPhone: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'On Hold' | 'Closed' | 'Escalated';
  strategyId: string;
  assignedTo?: string;
  notes: string;
  nextAction: string;
  nextActionDate: string;
}

interface Transaction {
  id: string;
  headerReference: string;
  invoiceDate: Date;
  dueDate: Date;
  type: TransactionType;
  amount: number;
  balance: number;
  status: 'Outstanding' | 'Overdue';
  daysOverdue: number;
}

type SortField = 'headerReference' | 'invoiceDate' | 'dueDate' | 'type' | 'amount' | 'balance' | 'status' | 'daysOverdue';
type SortDirection = 'asc' | 'desc' | null;

export function CreateCaseDrawer({ open, onOpenChange, onCaseCreated, prePopulatedCustomer, selectedTransactions: preSelectedTransactions }: Readonly<CreateCaseDrawerProps>) {
  const navigate = useNavigate();
  const createCaseMutation = useCreateCollectionCase();
  const [activeTab, setActiveTab] = useState("overview");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());
  const [contacts, setContacts] = useState<ContactUI[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const skipChangeDetection = useRef(false);

  // Fetch active users for the Assigned To dropdown
  const { data: users = [] } = useUsersList();
  const activeUsers = users.filter(user => user.status === 'active');
  

  // Form data state
  const [formData, setFormData] = useState<NewCaseData>({
    customerName: prePopulatedCustomer?.customerName || "",
    customerId: prePopulatedCustomer?.accountNumber || "",
    customerEmail: "",
    customerPhone: "",
    priority: "Medium",
    status: "Active",
    strategyId: "strategy-1", // Default to Standard Collection
    assignedTo: undefined,
    notes: "",
    nextAction: "Follow-up call",
    nextActionDate: format(new Date(), "yyyy-MM-dd"),
  });

  // Convert preSelectedTransactions to the Transaction format used in the drawer
  const availableTransactions: Transaction[] = preSelectedTransactions && preSelectedTransactions.length > 0
    ? preSelectedTransactions.map(t => {
        const dueDate = t.dueDate ? new Date(t.dueDate) : new Date();
        const today = new Date();
        const daysOverdue = t.status === 'overdue' 
          ? Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)))
          : 0;
        
        return {
          id: t.id.toString(),
          headerReference: t.headerReference || t.referenceNumber,
          invoiceDate: new Date(t.transactionDate),
          dueDate: dueDate,
          type: t.transactionType,
          amount: t.amount,
          balance: t.outstandingAmount || t.amount,
          status: t.status === 'overdue' ? 'Overdue' : 'Outstanding',
          daysOverdue: daysOverdue,
        };
      })
    : [
        // Fallback mock data if no transactions are provided
        {
          id: "1",
          headerReference: "INV-2024-001",
          invoiceDate: new Date(2024, 0, 15),
          dueDate: new Date(2024, 1, 15),
          type: "invoice",
          amount: 6000,
          balance: 6000,
          status: "Overdue",
          daysOverdue: 45,
        },
        {
          id: "2",
          headerReference: "INV-2024-002",
          invoiceDate: new Date(2024, 1, 1),
          dueDate: new Date(2024, 2, 1),
          type: "invoice",
          amount: 5250,
          balance: 5250,
          status: "Overdue",
          daysOverdue: 30,
        },
        {
          id: "3",
          headerReference: "INV-2024-003",
          invoiceDate: new Date(2024, 2, 10),
          dueDate: new Date(2024, 3, 10),
          type: "invoice",
          amount: 3750,
          balance: 3750,
          status: "Outstanding",
          daysOverdue: 0,
        },
      ];

  // Mock contacts for the customer
  const mockContacts: ContactUI[] = [
    {
      id: "contact-1",
      firstName: "John",
      surname: "Smith",
      email: "john.smith@example.com",
      phone: "+1 (555) 123-4567",
      jobTitle: "Accounts Payable Manager",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Primary contact for all payment-related matters",
      createdAt: new Date(2023, 0, 15).toISOString(),
      lastContact: new Date(2024, 2, 20).toISOString(),
      isPrimary: true,
    },
    {
      id: "contact-2",
      firstName: "Sarah",
      surname: "Johnson",
      email: "sarah.johnson@example.com",
      phone: "+1 (555) 123-4568",
      jobTitle: "Finance Director",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Escalation contact for urgent matters",
      createdAt: new Date(2023, 1, 10).toISOString(),
      lastContact: new Date(2024, 2, 15).toISOString(),
      isPrimary: false,
    },
    {
      id: "contact-3",
      firstName: "Michael",
      surname: "Chen",
      email: "michael.chen@example.com",
      phone: "+1 (555) 123-4569",
      jobTitle: "Accounts Payable Clerk",
      company: "Example Corp",
      customerId: "1",
      address: "123 Business St, New York, NY 10001",
      notes: "Day-to-day invoice queries",
      createdAt: new Date(2023, 3, 5).toISOString(),
      lastContact: new Date(2024, 2, 18).toISOString(),
      isPrimary: false,
    },
  ];

  // Auto-populate form when drawer opens with selected transactions
  useEffect(() => {
    if (open && preSelectedTransactions && preSelectedTransactions.length > 0) {
      skipChangeDetection.current = true;
      const firstTransaction = preSelectedTransactions[0];
      
      // Auto-populate customer details from first transaction
      setFormData(prev => ({
        ...prev,
        customerName: firstTransaction.customerName || "",
        customerId: `ACC-${firstTransaction.customerId.toString().padStart(3, '0')}`,
        // Note: Email and phone would ideally come from customer data
        // For now, we'll leave them empty to be filled manually or from contacts
      }));

      // Auto-select the transactions
      const transactionIds = new Set(preSelectedTransactions.map(t => t.id.toString()));
      setSelectedTransactions(transactionIds);

      // Load mock contacts for the customer
      setContacts(mockContacts);
      // Auto-select primary contact if available
      const primaryContact = mockContacts.find(c => c.isPrimary);
      if (primaryContact) {
        setSelectedContactId(primaryContact.id);
        setFormData(prev => ({
          ...prev,
          customerEmail: primaryContact.email || "",
          customerPhone: primaryContact.phone || "",
        }));
      }
    }
  }, [open, preSelectedTransactions]);

  // Track unsaved changes
  useEffect(() => {
    if (skipChangeDetection.current) {
      skipChangeDetection.current = false;
      return;
    }

    const hasChanges = 
      formData.customerEmail.trim() !== "" ||
      formData.customerPhone.trim() !== "" ||
      formData.notes.trim() !== "" ||
      selectedTransactions.size > 0 ||
      formData.priority !== "Medium" ||
      formData.status !== "Active" ||
      formData.strategyId !== "strategy-1" ||
      formData.assignedTo !== undefined ||
      formData.nextAction !== "Follow-up call";
    
    setHasUnsavedChanges(hasChanges);
  }, [formData, selectedTransactions]);

  // Handle customer selection from combobox
  const handleCustomerSelect = (customer: CreatePromiseFormCustomer) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      customerId: customer.accountNumber,
      customerName: customer.name,
    });
    setHasUnsavedChanges(true);
  };

  // Reset form when dialog opens/closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Check for unsaved changes before closing
      if (hasUnsavedChanges) {
        setShowCloseConfirmation(true);
        return;
      }
      
      // Reset form
      setFormData({
        customerName: prePopulatedCustomer?.customerName || "",
        customerId: prePopulatedCustomer?.accountNumber || "",
        customerEmail: "",
        customerPhone: "",
        priority: "Medium",
        status: "Active",
        strategyId: "strategy-1", // Reset to default
        assignedTo: undefined,
        notes: "",
        nextAction: "Follow-up call",
        nextActionDate: format(new Date(), "yyyy-MM-dd"),
      });
      setSelectedTransactions(new Set());
      setActiveTab("overview");
      setSelectedContactId("");
      setContacts(mockContacts);
      setSelectedCustomer(null);
      setHasUnsavedChanges(false);
      setSortField(null);
      setSortDirection(null);
    } else {
      // Load contacts when opening
      setContacts(mockContacts);
      
      // Auto-populate primary contact if customer is pre-populated
      if (prePopulatedCustomer) {
        skipChangeDetection.current = true;
        const primaryContact = mockContacts.find((c) => c.isPrimary);
        if (primaryContact) {
          setSelectedContactId(primaryContact.id);
          setFormData({
            customerName: prePopulatedCustomer.customerName,
            customerId: prePopulatedCustomer.accountNumber,
            customerEmail: primaryContact.email,
            customerPhone: primaryContact.phone,
            priority: "Medium",
            status: "Active",
            strategyId: "strategy-1",
            assignedTo: undefined,
            notes: "",
            nextAction: "Follow-up call",
            nextActionDate: format(new Date(), "yyyy-MM-dd"),
          });
        }
      }
    }
    onOpenChange(open);
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    setHasUnsavedChanges(false);
    
    // Reset form
    setFormData({
      customerName: prePopulatedCustomer?.customerName || "",
      customerId: prePopulatedCustomer?.accountNumber || "",
      customerEmail: "",
      customerPhone: "",
      priority: "Medium",
      status: "Active",
      strategyId: "strategy-1",
      assignedTo: undefined,
      notes: "",
      nextAction: "Follow-up call",
      nextActionDate: format(new Date(), "yyyy-MM-dd"),
    });
    setSelectedTransactions(new Set());
    setActiveTab("overview");
    setSelectedContactId("");
    setContacts(mockContacts);
    setSelectedCustomer(null);
    setSortField(null);
    setSortDirection(null);
    
    onOpenChange(false);
  };

  const handleContactChange = (contactId: string) => {
    setSelectedContactId(contactId);
    
    if (contactId === "none") {
      // Clear email and phone when "None" is selected
      setFormData({
        ...formData,
        customerEmail: "",
        customerPhone: "",
      });
    } else {
      // Find the selected contact and auto-populate
      const selectedContact = contacts.find((c) => c.id === contactId);
      if (selectedContact) {
        setFormData({
          ...formData,
          customerEmail: selectedContact.email,
          customerPhone: selectedContact.phone,
        });
      }
    }
  };

  const handleToggleTransaction = (transactionId: string) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedTransactions = () => {
    if (!sortField || !sortDirection) {
      return availableTransactions;
    }

    return [...availableTransactions].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'headerReference':
          aValue = a.headerReference;
          bValue = b.headerReference;
          break;
        case 'invoiceDate':
          aValue = a.invoiceDate.getTime();
          bValue = b.invoiceDate.getTime();
          break;
        case 'dueDate':
          aValue = a.dueDate.getTime();
          bValue = b.dueDate.getTime();
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        case 'balance':
          aValue = a.balance;
          bValue = b.balance;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'daysOverdue':
          aValue = a.daysOverdue;
          bValue = b.daysOverdue;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const validateForm = (): boolean => {
    // Required fields validation
    if (!formData.customerId.trim() || !formData.customerName.trim()) {
      toast.error("Please select a customer");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerEmail.trim()) {
      toast.error("Customer email is required");
      setActiveTab("overview");
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customerEmail)) {
      toast.error("Please enter a valid email address");
      setActiveTab("overview");
      return false;
    }

    if (!formData.customerPhone.trim()) {
      toast.error("Customer phone is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextAction.trim()) {
      toast.error("Next action is required");
      setActiveTab("overview");
      return false;
    }

    if (!formData.nextActionDate) {
      toast.error("Next action date is required");
      setActiveTab("overview");
      return false;
    }

    // At least one transaction must be selected
    if (selectedTransactions.size === 0) {
      toast.error("Please select at least one transaction");
      setActiveTab("transactions");
      return false;
    }

    // Assigned To is required
    if (!formData.assignedTo) {
      toast.error("Assigned To is required");
      setActiveTab("overview");
      return false;
    }

    return true;
  };

  const handleSaveCase = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const totalAmount = availableTransactions
        .filter(x => selectedTransactions.has(x.id))
        .reduce((sum, x) => sum + x.balance, 0);

      // Use original numeric IDs from preSelectedTransactions when available
      const transactionIds = preSelectedTransactions && preSelectedTransactions.length > 0
        ? preSelectedTransactions
            .filter(t => selectedTransactions.has(String(t.id)))
            .map(t => t.id)
        : availableTransactions
            .filter(t => selectedTransactions.has(t.id))
            .map(t => parseInt(t.id, 10))
            .filter(id => !isNaN(id));

      // Resolve customer ID: prefer preSelectedTransactions (already numeric), then prePopulatedCustomer, then combobox selection
      const numericCustomerId = preSelectedTransactions?.[0]?.customerId
        ?? (prePopulatedCustomer ? parseInt(prePopulatedCustomer.customerId, 10) : null)
        ?? (selectedCustomer ? parseInt(selectedCustomer.id, 10) : NaN);

      if (isNaN(numericCustomerId)) {
        toast.error("Unable to determine customer ID");
        return;
      }

      // Drawer creates manual-only cases (no strategy)
      const response = await createCaseMutation.mutateAsync({
        customerId: numericCustomerId,
        priorityId: mapPriorityToId(formData.priority),
        strategyId: undefined,
        amount: totalAmount,
        nextAction: formData.nextAction,
        nextActionDate: formData.nextActionDate || undefined,
        notes: formData.notes || undefined,
        transactionIds,
        assignedToUserId: formData.assignedTo ? Number(formData.assignedTo) : undefined,
      });

      toast.success('Collection case created', {
        description: (
          <span>
            Case{' '}
            <a
              href={`/collections/queue/case/${response.caseId}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/collections/queue/case/${response.caseId}`);
              }}
              style={{ color: '#0d9488', textDecoration: 'underline', fontWeight: 500 }}
            >
              {response.caseNumber}
            </a>
            {' '}created successfully
          </span>
        ),
        duration: 8000,
      });

      if (onCaseCreated) {
        onCaseCreated();
      }

      // Reset form and close directly, bypassing unsaved changes check
      setHasUnsavedChanges(false);
      setFormData({
        customerName: prePopulatedCustomer?.customerName || "",
        customerId: prePopulatedCustomer?.accountNumber || "",
        customerEmail: "",
        customerPhone: "",
        priority: "Medium",
        status: "Active",
        strategyId: "strategy-1",
        assignedTo: undefined,
        notes: "",
        nextAction: "Follow-up call",
        nextActionDate: format(new Date(), "yyyy-MM-dd"),
      });
      setSelectedTransactions(new Set());
      setActiveTab("overview");
      setSelectedContactId("");
      setContacts(mockContacts);
      setSelectedCustomer(null);
      setSortField(null);
      setSortDirection(null);
      onOpenChange(false);
    } catch (error) {
      toast.error(`Failed to create case: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Escalated":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  const calculateTotalAmount = () => {
    return availableTransactions
      .filter(t => selectedTransactions.has(t.id))
      .reduce((sum, t) => sum + t.balance, 0);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0 flex flex-col" hideCloseButton>
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div>
                <SheetTitle className="text-3xl font-bold tracking-tight">Create New Case</SheetTitle>
                <p className="text-muted-foreground mt-1">
                  Enter customer and case information to create a new collection case
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="default"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
                className="gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button
                size="default"
                onClick={handleSaveCase}
                disabled={isSaving}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Creating..." : "Create Case"}
              </Button>
            </div>
          </div>
        </SheetHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-4 mx-6 mt-4 shrink-0">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
            <TabsTrigger value="audit">Audit Log</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 pb-6 mt-6">
            <TabsContent value="overview" className="space-y-6 mt-0">
              {/* Two Column Layout - Customer & Case Info */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Customer Information & Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Contact details and customer profile</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {/* Customer Selection */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer <span className="text-red-500">*</span></p>
                        {(prePopulatedCustomer || (preSelectedTransactions && preSelectedTransactions.length > 0)) ? (
                          <div className="space-y-2">
                            <p className="text-lg font-semibold">{formData.customerId} {formData.customerName}</p>
                            <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                          </div>
                        ) : (
                          <CustomerSearchCombobox
                            selectedCustomer={selectedCustomer}
                            onSelectCustomer={handleCustomerSelect}
                            placeholder="Select a customer..."
                          />
                        )}
                      </div>
                      
                      <Separator />
                      
                      {/* Contact Name */}
                      <div className="space-y-2">
                        <Label htmlFor="contact" className="text-xs font-medium text-muted-foreground">
                          Contact Name
                        </Label>
                        <Select
                          value={selectedContactId}
                          onValueChange={handleContactChange}
                        >
                          <SelectTrigger id="contact">
                            <SelectValue placeholder="Select a contact to auto-populate email and phone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {contacts.map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {`${contact.firstName} ${contact.surname}`}
                                {contact.isPrimary && " (Primary)"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Separator />

                      {/* Email Address */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.customerEmail}
                          onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                          placeholder="customer@example.com"
                        />
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Phone Number <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.customerPhone}
                          onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Case Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Case Management
                    </CardTitle>
                    <CardDescription>Case details and configuration</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-6 md:grid-cols-2">
                      {/* First Column */}
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Outstanding Amount</p>
                          <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                            {formatCurrency(calculateTotalAmount(), preSelectedTransactions?.[0]?.currency || 'GBP')}
                          </p>
                          <p className="text-xs text-muted-foreground">From selected transactions</p>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="assignedTo" className="text-xs font-medium text-muted-foreground">
                            Assigned To
                          </Label>
                          <Select
                            value={formData.assignedTo || "unassigned"}
                            onValueChange={(value) => 
                              setFormData({ ...formData, assignedTo: value === "unassigned" ? undefined : value })
                            }
                          >
                            <SelectTrigger id="assignedTo">
                              <SelectValue placeholder="Select user..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {activeUsers.map((user) => (
                                <SelectItem key={user.id} value={user.id}>
                                  <div className="flex items-center gap-2">
                                    <Avatar className="h-5 w-5">
                                      <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                      <AvatarFallback className="text-[10px]">
                                        {user.firstName[0]}{user.lastName[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span>{user.firstName} {user.lastName}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Separator />

                        <div className="space-y-2">
                          <Label htmlFor="status" className="text-xs font-medium text-muted-foreground">
                            Case Status
                          </Label>
                          <Select
                            value={formData.status}
                            onValueChange={(value: 'Active' | 'On Hold' | 'Closed') => 
                              setFormData({ ...formData, status: value })
                            }
                          >
                            <SelectTrigger id="status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Escalated">Escalated</SelectItem>
                              <SelectItem value="On Hold">On Hold</SelectItem>
                              <SelectItem value="Closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="priority" className="text-xs font-medium text-muted-foreground">
                            Priority Level
                          </Label>
                          <Select
                            value={formData.priority}
                            onValueChange={(value: 'High' | 'Medium' | 'Low') => 
                              setFormData({ ...formData, priority: value })
                            }
                          >
                            <SelectTrigger id="priority">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="High">High</SelectItem>
                              <SelectItem value="Medium">Medium</SelectItem>
                              <SelectItem value="Low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Second Column - Empty for now, matching Case Details structure */}
                      <div className="space-y-3">
                        {/* This column is intentionally left empty to match the Case Details layout */}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Next Action - Call to Action Style */}
              <Card className="border-2 border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Next Scheduled Action
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="nextAction" className="text-xs font-medium text-muted-foreground">
                        Action Type <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.nextAction}
                        onValueChange={(value) => setFormData({ ...formData, nextAction: value })}
                      >
                        <SelectTrigger id="nextAction">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Follow-up call">Follow-up call</SelectItem>
                          <SelectItem value="Send email">Send email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nextActionDate" className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Due Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nextActionDate"
                        type="date"
                        value={formData.nextActionDate}
                        onChange={(e) => setFormData({ ...formData, nextActionDate: e.target.value })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    Case Notes
                  </CardTitle>
                  <CardDescription>Add notes about this case</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Add notes about this case..."
                    rows={4}
                    className="resize-none"
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="transactions" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Outstanding Transactions</CardTitle>
                      <CardDescription>
                        Select transactions to include in this case ({selectedTransactions.size} selected)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead 
                          className="cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('headerReference')}
                        >
                          <div className="flex items-center">
                            Reference Number
                            {getSortIcon('headerReference')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('invoiceDate')}
                        >
                          <div className="flex items-center">
                            Date
                            {getSortIcon('invoiceDate')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('dueDate')}
                        >
                          <div className="flex items-center">
                            Due Date
                            {getSortIcon('dueDate')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('type')}
                        >
                          <div className="flex items-center">
                            Type
                            {getSortIcon('type')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('amount')}
                        >
                          <div className="flex items-center justify-end">
                            Amount
                            {getSortIcon('amount')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('balance')}
                        >
                          <div className="flex items-center justify-end">
                            Balance
                            {getSortIcon('balance')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center">
                            Status
                            {getSortIcon('status')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer select-none hover:bg-muted/50"
                          onClick={() => handleSort('daysOverdue')}
                        >
                          <div className="flex items-center justify-end">
                            Days Overdue
                            {getSortIcon('daysOverdue')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getSortedTransactions().map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50",
                            selectedTransactions.has(transaction.id) && "bg-muted/30"
                          )}
                          onClick={() => handleToggleTransaction(transaction.id)}
                        >
                          <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedTransactions.has(transaction.id)}
                              onCheckedChange={() => handleToggleTransaction(transaction.id)}
                              aria-label={`Select ${transaction.headerReference}`}
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {transaction.headerReference}
                          </TableCell>
                          <TableCell>
                            {format(transaction.invoiceDate, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            {format(transaction.dueDate, "dd/MM/yyyy")}
                          </TableCell>
                          <TableCell>
                            <TransactionTypeBadge type={transaction.type} />
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(transaction.amount, preSelectedTransactions?.[0]?.currency || 'GBP')}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(transaction.balance, preSelectedTransactions?.[0]?.currency || 'GBP')}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                transaction.status === "Overdue"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {transaction.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {transaction.daysOverdue > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium">
                                {transaction.daysOverdue}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(calculateTotalAmount(), preSelectedTransactions?.[0]?.currency || 'GBP')}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Activity Timeline</CardTitle>
                  <CardDescription>
                    Activity history will appear here after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No activity yet</p>
                    <p className="text-xs mt-1">Activities will be logged once the case is created</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit" className="space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Audit trail will be available after the case is created
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit entries yet</p>
                    <p className="text-xs mt-1">All case modifications will be logged after creation</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>

      {/* Confirmation Dialog for Unsaved Changes */}
      <AlertDialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving? All your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
